import React, { useState, useRef, useEffect, useCallback, useMemo, useContext } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { masterSchemeList, orderNew, getUccV2 } from "../../api/bseApi";
import { buildOrderNewPurchasePayload, resolveUccOrderModes } from "../../utils/bseOrder";
import LocContext from "../context/locContext";

const TEAL = "#06b6a4";
const TEST_CLIENT_CODE = "DematUCCTest9";
const DEFAULT_MEMBER_ID = process.env.REACT_APP_BSE_MEMBER_ID || "63164";
const PAGE_SIZE = 100;

const TABLE_HEADERS = [
  { key: "scheme", label: "Scheme", sort: true },
  { key: "schemeCode", label: "BSE Code", sort: true },
  { key: "fund", label: "Fund", sort: true },
  { key: "subCategory", label: "Category", sort: true },
  { key: "plan", label: "Plan", sort: true },
  { key: "option", label: "Option", sort: true },
  { key: "actions", label: "Actions", sort: false },
];

const inputStyle = {
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  padding: "8px 12px",
  fontSize: "14px",
  width: "100%",
  background: "#fff",
};

function mapBseSchemeToRow(scheme) {
  const schemeCode =
    scheme.scheme_bse_code ||
    scheme.bse_code ||
    scheme.scheme_code ||
    scheme.scheme_cd ||
    "";
  const schemeName = scheme.name || scheme.parent_scheme_name || scheme.scheme_name || "Unknown Scheme";
  const id = schemeCode || scheme.scheme_isin || scheme.scheme_amfi_code || schemeName;

  return {
    id,
    scheme: schemeName,
    schemeCode,
    fund: scheme.scheme_amc_name || "N/A",
    subCategory: scheme.scheme_sub_category || scheme.scheme_category || "N/A",
    plan: scheme.scheme_plan || "N/A",
    option: scheme.scheme_option || "N/A",
    rawScheme: scheme,
  };
}

function extractApiError(err) {
  const body = err?.response?.data || {};
  const nested = body.data || {};
  const messages = body.messages || nested.messages;
  if (Array.isArray(messages) && messages.length) {
    return messages
      .map((m) => {
        const field = m?.field ? ` (${m.field})` : "";
        return `${m?.errcode || m?.msg_text || m?.message || "BSE error"}${field}`;
      })
      .join("<br/>");
  }
  return body.message || err?.message || "Order creation failed";
}

function buildBseMobile(countryCode, phone) {
  const mobile = String(phone || "").replace(/\D/g, "").slice(-10);
  if (!mobile) return "";
  const country = String(countryCode || "91").replace(/\D/g, "") || "91";
  return `+${country}${mobile}`;
}

function generateNumericOrderRef() {
  return `${Date.now()}${Math.floor(Math.random() * 9000 + 1000)}`.slice(0, 16);
}

export default function NewInvestment() {
  const { userData } = useContext(LocContext);
  const clientUcc = useMemo(() => TEST_CLIENT_CODE, []);
  const [schemeSelect, setSchemeSelect] = useState("");
  const [filterType, setFilterType] = useState("fund"); // "fund" | "category"
  const [fundSearch, setFundSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [selectedFund, setSelectedFund] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [tab, setTab] = useState("all_schemes"); // "all_schemes" | "nfo_schemes"
  const [schemes, setSchemes] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [schemeError, setSchemeError] = useState("");
  const [orderAmount, setOrderAmount] = useState("1000");
  const [placingKey, setPlacingKey] = useState("");
  const [selectedOrderScheme, setSelectedOrderScheme] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [orderForm, setOrderForm] = useState({
    euinOpted: "N",
    euin: "",
    subBrokerArn: "",
    subBrokerCode: "",
    memOrdRefId: generateNumericOrderRef(),
    orderType: "p",
    orderAction: "place_now",
    mandateId: "",
    transactionMode: "d",
    transactionType: "fresh",
    folio: "",
    paymentRefId: "",
  });
  const [profileContact, setProfileContact] = useState({
    email: "",
    phone: "",
    countryCode: "91",
  });
  const [uccModes, setUccModes] = useState({
    loading: true,
    allowed: ["d"],
    hint: "",
    defaultMode: "d",
  });
  const filterDropdownRef = useRef(null);
  const orderTicketRef = useRef(null);

  useEffect(() => {
    const baseContact = {
      email: userData?.email || "",
      phone: userData?.phone || "",
      countryCode: userData?.country_code || "91",
    };
    setProfileContact(baseContact);

    if (!userData?.id) return;

    const loadProfileContact = async () => {
      try {
        const baseUrl = String(process.env.REACT_APP_BACKEND_BASE_URL || "").replace(/\/+$/, "");
        const res = await axios.get(`${baseUrl}/user/${userData.id}`);
        const profile = res?.data?.data || {};
        setProfileContact({
          email: profile.email || baseContact.email,
          phone: profile.phone || baseContact.phone,
          countryCode: profile.country_code || baseContact.countryCode || "91",
        });
      } catch (_err) {
        setProfileContact(baseContact);
      }
    };

    loadProfileContact();
  }, [userData]);

  useEffect(() => {
    let cancelled = false;
    const loadUccModes = async () => {
      setUccModes((prev) => ({ ...prev, loading: true }));
      try {
        const res = await getUccV2({
          data: {
            member: { member_id: DEFAULT_MEMBER_ID },
            investor: { client_code: clientUcc },
          },
        });
        const uccData = res?.data?.data || {};
        const modes = resolveUccOrderModes(uccData);
        if (cancelled) return;
        setUccModes({
          loading: false,
          allowed: modes.allowed.length ? modes.allowed : ["d"],
          hint: modes.hint,
          defaultMode: modes.defaultMode,
        });
        setOrderForm((prev) => ({
          ...prev,
          transactionMode: modes.allowed.includes(prev.transactionMode)
            ? prev.transactionMode
            : modes.defaultMode,
        }));
      } catch (_err) {
        if (cancelled) return;
        setUccModes({
          loading: false,
          allowed: ["d"],
          hint: "Could not load UCC profile. Defaulting to Demat mode.",
          defaultMode: "d",
        });
        setOrderForm((prev) => ({ ...prev, transactionMode: "d" }));
      }
    };
    loadUccModes();
    return () => {
      cancelled = true;
    };
  }, [clientUcc]);

  const fundOptions = useMemo(
    () => [...new Set(schemes.map((s) => s.fund).filter(Boolean))].sort(),
    [schemes]
  );
  const categoryOptions = useMemo(
    () => [...new Set(schemes.map((s) => s.subCategory).filter(Boolean))].sort(),
    [schemes]
  );

  const filteredFunds = fundOptions.filter((f) =>
    f.toLowerCase().includes((fundSearch || "").toLowerCase())
  );
  const filteredCategories = categoryOptions.filter((c) =>
    c.toLowerCase().includes((categorySearch || "").toLowerCase())
  );

  const fetchSchemes = useCallback(async () => {
    setLoadingSchemes(true);
    setSchemeError("");
    try {
      const payload = {
        data: {
          fields: ["ALL"],
          count_only: false,
          start: 0,
          length: PAGE_SIZE,
          search: { value: "" },
          filter_param: {
            scheme_amc_name: selectedFund || "",
            scheme_category: "",
            scheme_sub_category: selectedCategory || "",
            scheme_option: "",
            scheme_plan: "",
            scheme_isin: "",
            settlement_days: "",
            transaction_allowed: "",
          },
        },
      };

      const res = await masterSchemeList(payload);
      const lists = Array.isArray(res?.data?.data?.lists) ? res.data.data.lists : [];
      const mapped = lists.map(mapBseSchemeToRow).filter((s) => s.schemeCode);
      setSchemes(mapped);
    } catch (err) {
      setSchemes([]);
      setSchemeError(extractApiError(err));
    } finally {
      setLoadingSchemes(false);
    }
  }, [selectedCategory, selectedFund]);

  useEffect(() => {
    fetchSchemes();
  }, [fetchSchemes]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setFilterDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayFilterValue = filterType === "fund" ? (selectedFund || "Select Fund") : (selectedCategory || "Select Category");
  const filterList = filterType === "fund" ? filteredFunds : filteredCategories;
  const searchValue = filterType === "fund" ? fundSearch : categorySearch;
  const setSearchValue = filterType === "fund" ? setFundSearch : setCategorySearch;
  const onSelectItem = filterType === "fund"
    ? (name) => { setSelectedFund(name); setSchemeSelect(""); setFilterDropdownOpen(false); setFundSearch(""); }
    : (name) => { setSelectedCategory(name); setSchemeSelect(""); setFilterDropdownOpen(false); setCategorySearch(""); };

  const selectedSchemeFromDropdown = useMemo(
    () => schemes.find((s) => s.id === schemeSelect) || null,
    [schemeSelect, schemes]
  );

  useEffect(() => {
    if (selectedSchemeFromDropdown) {
      setSelectedOrderScheme(selectedSchemeFromDropdown);
    }
  }, [selectedSchemeFromDropdown]);

  const displayedSchemes = useMemo(() => {
    const selected = schemeSelect
      ? schemes.filter((s) => s.id === schemeSelect)
      : schemes;
    return tab === "nfo_schemes" ? [] : selected;
  }, [schemeSelect, schemes, tab]);

  const orderEmail = profileContact.email || userData?.email || "";
  const orderMobile = buildBseMobile(
    profileContact.countryCode || userData?.country_code,
    profileContact.phone || userData?.phone
  );

  const updateOrderForm = (patch) => {
    setOrderForm((prev) => ({ ...prev, ...patch }));
  };

  const handleTransact = (row) => {
    setSelectedOrderScheme(row);
    setSchemeSelect(row.id);
    setTermsAccepted(false);
    updateOrderForm({ memOrdRefId: generateNumericOrderRef() });
    setTimeout(() => {
      orderTicketRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const submitOrder = async () => {
    const row = selectedOrderScheme;
    const amount = Number(orderAmount);
    if (!row?.schemeCode) {
      Swal.fire({ icon: "warning", title: "Select a valid BSE scheme" });
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      Swal.fire({ icon: "warning", title: "Enter a valid amount" });
      return;
    }
    if (!termsAccepted) {
      Swal.fire({
        icon: "warning",
        title: "Accept terms",
        text: "Please confirm scheme information, NAV applicability and payment modes before placing order.",
      });
      return;
    }
    if (!uccModes.loading && !uccModes.allowed.length) {
      Swal.fire({
        icon: "error",
        title: "UCC not ready for orders",
        text: uccModes.hint || "Complete physical or demat registration on BSE for this UCC.",
      });
      return;
    }

    const orderMode = uccModes.allowed.includes(orderForm.transactionMode)
      ? orderForm.transactionMode
      : uccModes.defaultMode;

    const confirm = await Swal.fire({
      icon: "question",
      title: "Create BSE order?",
      html: `
        <div class="text-start">
          <p><strong>UCC:</strong> ${clientUcc}</p>
          <p><strong>Mode:</strong> ${orderMode === "d" ? "Demat" : "Physical"}</p>
          <p><strong>Scheme:</strong> ${row.scheme}</p>
          <p><strong>BSE Code:</strong> ${row.schemeCode}</p>
          <p><strong>Amount:</strong> ₹${amount.toLocaleString("en-IN")}</p>
          <p><strong>Member Ref:</strong> ${orderForm.memOrdRefId}</p>
          <p><strong>Email:</strong> ${orderEmail || "-"}</p>
          <p><strong>Mobile:</strong> ${orderMobile || "-"}</p>
          <p class="small text-muted mb-0">This will call /api/bse/order_new.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Create order",
    });
    if (!confirm.isConfirmed) return;

    setPlacingKey(row.id);
    try {
      const payload = buildOrderNewPurchasePayload({
        clientCode: clientUcc,
        scheme: row.schemeCode,
        amount,
        memOrdRefId: orderForm.memOrdRefId,
        physOrDemat: orderMode,
        email: orderEmail,
        mobnum: orderMobile,
        folio: orderForm.folio,
        isFresh: orderForm.transactionType === "fresh",
        paymentRefId: orderForm.paymentRefId,
      });
      const order = payload.data.orders[0];
      order.is_units = false;
      order.min_redeem_flag = false;
      if (orderForm.euin || orderForm.subBrokerArn || orderForm.subBrokerCode) {
        order.mem_details = {
          euin: orderForm.euin,
          euin_flag: orderForm.euinOpted === "Y",
          sub_br_arn: orderForm.subBrokerArn,
          sub_br_code: orderForm.subBrokerCode,
          partner_id: "",
        };
      }

      const res = await orderNew(payload);
      const body = res?.data || {};
      const refs = body.mem_ord_ref_ids || payload.data.orders.map((o) => o.mem_ord_ref_id);

      Swal.fire({
        icon: "success",
        title: "Order request sent to BSE",
        html: `
          <div class="text-start">
            <p><strong>UCC:</strong> ${clientUcc}</p>
            <p><strong>Scheme:</strong> ${row.schemeCode}</p>
            <p><strong>Amount:</strong> ₹${amount.toLocaleString("en-IN")}</p>
            <p><strong>Email:</strong> ${orderEmail || "-"}</p>
            <p><strong>Mobile:</strong> ${orderMobile || "-"}</p>
            <p><strong>Reference:</strong> ${refs.join(", ")}</p>
          </div>
        `,
      });
      updateOrderForm({ memOrdRefId: generateNumericOrderRef() });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Order creation failed",
        html: extractApiError(err),
      });
    } finally {
      setPlacingKey("");
    }
  };

  return (
    <>
      <ClientNavbar />
      <div className="page-content ni-page" style={{ background: "#f1f5f9", minHeight: "100vh" }}>
        {/* Header */}
        <div className="ni-hero mb-4">
          <div>
            <div className="ni-eyebrow">BSE StAR MF Order Ticket</div>
            <h3 className="mb-2">New Investment</h3>
            <p className="mb-3">
              Search a scheme, review order details, then place a purchase order through BSE.
            </p>
            <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none" }}>
              <ol className="breadcrumb mb-0 ni-breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/overview">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <span>Invest Online BSE</span> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">New Investment</li>
              </ol>
            </nav>
          </div>
          <div className="ni-hero-panel">
            <span className="ni-status-dot" />
            <div>
              <div className="small text-uppercase fw-semibold">Testing UCC</div>
              <div className="fw-bold">{clientUcc}</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="ni-card ni-filter-card mb-4" style={{ overflow: "visible" }}>
          <div className="card-body p-4" style={{ overflow: "visible" }}>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">Find a Scheme</h5>
                <div className="text-muted small">Choose from BSE master scheme list and prefill the order ticket.</div>
              </div>
              <button
                type="button"
                className="btn btn-sm ni-soft-btn"
                onClick={fetchSchemes}
                disabled={loadingSchemes}
              >
                {loadingSchemes ? "Refreshing..." : "Refresh Schemes"}
              </button>
            </div>
            <div className="row g-3 align-items-end" style={{ overflow: "visible" }}>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Scheme</label>
                <div className="d-flex ni-input-shell" style={{ ...inputStyle, padding: "0", overflow: "hidden" }}>
                  <span className="d-flex align-items-center px-2" style={{ color: "#64748b" }}>
                    <i className="fa fa-filter" aria-hidden />
                  </span>
                  <select
                    className="form-select border-0 shadow-none"
                    value={schemeSelect}
                    onChange={(e) => setSchemeSelect(e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <option value="">Select Scheme</option>
                    {schemes.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.schemeCode} - {s.scheme}
                      </option>
                    ))}
                  </select>
                  <span className="d-flex align-items-center px-2" style={{ color: "#64748b" }}>
                    <i className="fa fa-chevron-down" aria-hidden />
                  </span>
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Filter</label>
                <div className="d-flex ni-input-shell" style={{ ...inputStyle, padding: "0", overflow: "hidden" }}>
                  <span className="d-flex align-items-center px-2" style={{ color: "#64748b" }}>
                    <i className="fa fa-filter" aria-hidden />
                  </span>
                  <select
                    className="form-select border-0 shadow-none"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <option value="fund">Fund</option>
                    <option value="category">Category</option>
                  </select>
                  <span className="d-flex align-items-center px-2" style={{ color: "#64748b" }}>
                    <i className="fa fa-chevron-down" aria-hidden />
                  </span>
                </div>
              </div>
              <div className="col-md-4" ref={filterDropdownRef} style={{ overflow: "visible" }}>
                <label className="form-label small text-muted mb-1">
                  {filterType === "fund" ? "Funds" : "Category"}
                </label>
                <div style={{ position: "relative", overflow: "visible" }}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                    onKeyDown={(e) => e.key === "Enter" && setFilterDropdownOpen(!filterDropdownOpen)}
                    className="ni-input-shell"
                    style={{ ...inputStyle, display: "flex", alignItems: "center", cursor: "pointer" }}
                  >
                    <span className="d-flex align-items-center px-2" style={{ color: "#64748b" }}>
                      <i className="fa fa-filter" aria-hidden />
                    </span>
                    <span className="flex-grow-1" style={{ color: displayFilterValue.startsWith("Select") ? "#94a3b8" : "#334155" }}>
                      {displayFilterValue}
                    </span>
                    <i className={`fa fa-chevron-${filterDropdownOpen ? "up" : "down"} px-2`} style={{ color: "#64748b" }} aria-hidden />
                  </div>
                  {filterDropdownOpen && (
                    <div
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        marginTop: 4,
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                        zIndex: 1050,
                        maxHeight: 280,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <input
                        type="text"
                        className="form-control border-0 rounded-0"
                        placeholder="Type to Search..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}
                      />
                      <div style={{ overflowY: "auto", maxHeight: 220 }}>
                        {filterList.length === 0 ? (
                          <div className="p-3 text-muted small">No matches</div>
                        ) : (
                          filterList.map((item) => (
                            <div
                              key={item}
                              role="button"
                              tabIndex={0}
                              onClick={() => onSelectItem(item)}
                              onKeyDown={(e) => e.key === "Enter" && onSelectItem(item)}
                              style={{
                                padding: "10px 12px",
                                cursor: "pointer",
                                fontSize: "14px",
                                color: "#334155",
                                borderBottom: "1px solid #f1f5f9",
                              }}
                              className="hover-bg-teal"
                            >
                              {item}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Amount</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={orderAmount}
                  onChange={(e) => setOrderAmount(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div className="col-md-12">
                <div className="ni-info-strip">
                  <span>UCC: <strong>{clientUcc}</strong></span>
                  <span>Profile: <strong>{orderEmail || "-"}</strong> / <strong>{orderMobile || "-"}</strong></span>
                  <span>Endpoint: <code>/api/bse/order_new</code></span>
                </div>
              </div>
            </div>
          </div>

          {selectedOrderScheme && (
            <div
              ref={orderTicketRef}
              className="ni-order-ticket mx-4 mb-4"
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <div
                className="ni-ticket-header px-4 py-3"
                style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}
              >
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <h5 className="mb-1" style={{ color: "#1e293b" }}>Place an Order</h5>
                    <div className="small text-muted">
                      Investment details → Your details → Payment & confirmation
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedOrderScheme(null)}
                  >
                    Close
                  </button>
                </div>
                <div className="ni-stepper mt-3">
                  {["Investment Details", "Your Details", "Order Details", "Payment & Confirmation"].map((step, index) => (
                    <div className="ni-step active" key={step}>
                      <span>{index + 1}</span>
                      {step}
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4">
                <div className="ni-section mb-4">
                  <h6 className="ni-section-title">Scheme Details</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Sub Scheme Name</label>
                      <input
                        className="form-control"
                        value={selectedOrderScheme.scheme}
                        readOnly
                        style={inputStyle}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Scheme Code</label>
                      <input
                        className="form-control"
                        value={selectedOrderScheme.schemeCode}
                        readOnly
                        style={inputStyle}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">ISIN</label>
                      <input
                        className="form-control"
                        value={selectedOrderScheme.rawScheme?.scheme_isin || ""}
                        readOnly
                        style={inputStyle}
                      />
                    </div>
                    <div className="col-12">
                      <div
                        className="p-3"
                        style={{ border: "1px solid #e2e8f0", borderRadius: "10px" }}
                      >
                        <div className="fw-semibold mb-2" style={{ color: TEAL }}>
                          Basic Details
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {[
                            `Parent Scheme Name: ${selectedOrderScheme.rawScheme?.parent_scheme_name || "-"}`,
                            `Category: ${selectedOrderScheme.subCategory || "-"}`,
                            `ISIN: ${selectedOrderScheme.rawScheme?.scheme_isin || "-"}`,
                            `BSE Scheme Code: ${selectedOrderScheme.schemeCode || "-"}`,
                            `RTA Scheme Code: ${selectedOrderScheme.rawScheme?.rta_scheme_code || selectedOrderScheme.rawScheme?.scheme_rta_code || "-"}`,
                            `Growth/IDCW option: ${selectedOrderScheme.option || "-"}`,
                          ].map((item) => (
                            <span key={item} className="ni-basic-chip">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ni-section mb-4">
                  <h6 className="ni-section-title">Member Details</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">EUIN Opted</label>
                      <div className="d-flex gap-3 pt-2">
                        <label className="small">
                          <input
                            type="radio"
                            name="euinOpted"
                            className="me-1"
                            checked={orderForm.euinOpted === "Y"}
                            onChange={() => updateOrderForm({ euinOpted: "Y" })}
                          />
                          Yes
                        </label>
                        <label className="small">
                          <input
                            type="radio"
                            name="euinOpted"
                            className="me-1"
                            checked={orderForm.euinOpted === "N"}
                            onChange={() => updateOrderForm({ euinOpted: "N", euin: "" })}
                          />
                          No
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">EUIN</label>
                      <input
                        className="form-control"
                        placeholder="Eg. E123456"
                        value={orderForm.euin}
                        disabled={orderForm.euinOpted !== "Y"}
                        onChange={(e) => updateOrderForm({ euin: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Member Order Ref.ID</label>
                      <input
                        className="form-control"
                        value={orderForm.memOrdRefId}
                        onChange={(e) =>
                          updateOrderForm({
                            memOrdRefId: e.target.value.replace(/\D/g, "").slice(0, 16),
                          })
                        }
                        style={inputStyle}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Sub Broker ARN</label>
                      <input
                        className="form-control"
                        placeholder="Eg. ARN-01234"
                        value={orderForm.subBrokerArn}
                        onChange={(e) => updateOrderForm({ subBrokerArn: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Sub Broker Code</label>
                      <input
                        className="form-control"
                        placeholder="Eg. IN0020160027"
                        value={orderForm.subBrokerCode}
                        onChange={(e) => updateOrderForm({ subBrokerCode: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                <div className="ni-section mb-4">
                  <h6 className="ni-section-title">Client Details</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Client Code</label>
                      <input className="form-control" value={clientUcc} readOnly style={inputStyle} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Mobile Number</label>
                      <input className="form-control" value={orderMobile} readOnly style={inputStyle} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Email Address</label>
                      <input className="form-control" value={orderEmail} readOnly style={inputStyle} />
                    </div>
                  </div>
                </div>

                <div className="ni-section mb-4">
                  <h6 className="ni-section-title">Order Details</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Order Type</label>
                      <select
                        className="form-select"
                        value={orderForm.orderType}
                        onChange={(e) => updateOrderForm({ orderType: e.target.value })}
                        style={inputStyle}
                      >
                        <option value="p">Purchase</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Order Action</label>
                      <div className="d-flex gap-3 pt-2">
                        {[
                          ["place_now", "Place Now"],
                          ["schedule", "Schedule"],
                          ["cart", "Cart"],
                        ].map(([value, label]) => (
                          <label className="small" key={value}>
                            <input
                              type="radio"
                              name="orderAction"
                              className="me-1"
                              checked={orderForm.orderAction === value}
                              disabled={value !== "place_now"}
                              onChange={() => updateOrderForm({ orderAction: value })}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Mandate ID</label>
                      <input
                        className="form-control"
                        placeholder="Search"
                        value={orderForm.mandateId}
                        onChange={(e) => updateOrderForm({ mandateId: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                <div className="ni-section mb-4">
                  <h6 className="ni-section-title">Purchase Details</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Transaction Mode</label>
                      <select
                        className="form-select"
                        value={orderForm.transactionMode}
                        onChange={(e) => updateOrderForm({ transactionMode: e.target.value })}
                        style={inputStyle}
                        disabled={uccModes.loading || uccModes.allowed.length <= 1}
                      >
                        {uccModes.allowed.includes("p") && <option value="p">Physical</option>}
                        {uccModes.allowed.includes("d") && <option value="d">Demat</option>}
                      </select>
                      {uccModes.loading ? (
                        <div className="small text-muted mt-1">Loading UCC order modes...</div>
                      ) : uccModes.hint ? (
                        <div
                          className={`small mt-1 ${
                            uccModes.allowed.length === 1 && uccModes.allowed[0] === "d"
                              ? "text-info"
                              : "text-warning"
                          }`}
                        >
                          {uccModes.hint}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Transaction Type</label>
                      <div className="d-flex gap-3 pt-2">
                        <label className="small">
                          <input
                            type="radio"
                            name="transactionType"
                            className="me-1"
                            checked={orderForm.transactionType === "fresh"}
                            onChange={() => updateOrderForm({ transactionType: "fresh", folio: "" })}
                          />
                          Fresh
                        </label>
                        <label className="small">
                          <input
                            type="radio"
                            name="transactionType"
                            className="me-1"
                            checked={orderForm.transactionType === "additional"}
                            onChange={() => updateOrderForm({ transactionType: "additional" })}
                          />
                          Additional
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Folio</label>
                      <input
                        className="form-control"
                        placeholder="Eg. 12345678/90"
                        value={orderForm.folio}
                        disabled={orderForm.transactionType === "fresh"}
                        onChange={(e) => updateOrderForm({ folio: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">Amount</label>
                      <div className="input-group">
                        <span className="input-group-text">INR</span>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={orderAmount}
                          onChange={(e) => setOrderAmount(e.target.value)}
                          style={{ ...inputStyle, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small text-muted mb-1">PG/Bank Reference No</label>
                      <input
                        className="form-control"
                        placeholder="Eg. R14663HJU"
                        value={orderForm.paymentRefId}
                        onChange={(e) => updateOrderForm({ paymentRefId: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                <div className="ni-sticky-actions d-flex justify-content-between align-items-center flex-wrap gap-3 border-top pt-3">
                  <label className="small text-muted mb-0">
                    <input
                      type="checkbox"
                      className="me-2"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    I / we have read the terms and conditions, scheme information document,
                    NAV applicability and payment modes.
                  </label>
                  <button
                    type="button"
                    className="btn"
                    style={{
                      background: termsAccepted ? TEAL : "#94a3b8",
                      color: "#fff",
                      borderRadius: "8px",
                      fontWeight: 700,
                      minWidth: 160,
                    }}
                    onClick={submitOrder}
                    disabled={!termsAccepted || placingKey === selectedOrderScheme.id}
                  >
                    {placingKey === selectedOrderScheme.id ? "Placing..." : "Place Order"}
                  </button>
                </div>
              </div>
            </div>
          )}
      

        {/* Tabs */}
        <div className="d-flex gap-2 mb-4 mt-3" style={{ paddingLeft: "18px" }}>
          <button
            type="button"
            className={`ni-tab-btn ${tab === "all_schemes" ? "active" : ""}`}
            style={{
              background: tab === "all_schemes" ? TEAL : "#0d6efd",
              borderColor: tab === "all_schemes" ? TEAL : "#0d6efd",
            }}
            onClick={() => setTab("all_schemes")}
          >
            All Schemes
          </button>
          <button
            type="button"
            className={`ni-tab-btn ${tab === "nfo_schemes" ? "active" : ""}`}
            style={{
              background: tab === "nfo_schemes" ? TEAL : "#0d6efd",
              borderColor: tab === "nfo_schemes" ? TEAL : "#0d6efd",
            }}
            onClick={() => setTab("nfo_schemes")}
          >
            NFO Schemes
          </button>
        </div>

        {schemeError && (
          <div className="alert alert-danger mx-4" role="alert">
            {schemeError}
          </div>
        )}

        {/* Schemes table */}

          <div className="ni-table-card table-responsive">
            <div className="ni-table-toolbar">
              <div>
                <h5 className="mb-1">Available Schemes</h5>
                <div className="text-muted small">
                  {loadingSchemes ? "Loading schemes..." : `Showing ${displayedSchemes.length} schemes`}
                </div>
              </div>
            </div>
            <table className="table table-hover mb-0">
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  {TABLE_HEADERS.map((h) => (
                    <th
                      key={h.key}
                      style={{
                        padding: "12px 16px",
                        color: "#475569",
                        fontWeight: "600",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h.label}
                      {h.sort && <i className="fa fa-sort ms-1 opacity-75" style={{ fontSize: "10px" }} aria-hidden />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ background: "#fff" }}>
                {loadingSchemes && (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      Loading BSE schemes...
                    </td>
                  </tr>
                )}
                {!loadingSchemes && displayedSchemes.map((row, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 1 ? "#fafafa" : "#fff" }}>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ color: TEAL, fontWeight: "500", cursor: "pointer" }}>{row.scheme}</span>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#475569" }}>{row.schemeCode}</td>
                    <td style={{ padding: "12px 16px", color: "#475569" }}>{row.fund}</td>
                    <td style={{ padding: "12px 16px", color: "#475569" }}>{row.subCategory}</td>
                    <td style={{ padding: "12px 16px", color: "#475569" }}>{row.plan}</td>
                    <td style={{ padding: "12px 16px", color: "#475569" }}>{row.option}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          background: TEAL,
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          fontWeight: "600",
                          fontSize: "13px",
                          padding: "6px 14px",
                        }}
                        onClick={() => handleTransact(row)}
                        disabled={placingKey === row.id}
                      >
                        {placingKey === row.id ? "Placing..." : "Transact"}
                      </button>
                    </td>
                  </tr>
                ))}
                {!loadingSchemes && (tab === "nfo_schemes" || displayedSchemes.length === 0) && (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      {tab === "nfo_schemes" ? "No NFO schemes available." : "No schemes found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>{`
        .ni-page {
          padding-bottom: 40px;
          color: #1e293b;
        }

        .ni-hero {
          display: flex;
          justify-content: space-between;
          gap: 18px;
          align-items: stretch;
          padding: 28px;
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.65);
          background:
            radial-gradient(circle at top right, rgba(6,182,164,0.18), transparent 32%),
            linear-gradient(135deg, #ffffff 0%, #eefdfa 100%);
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
        }

        .ni-hero h3 {
          color: #0f766e;
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .ni-hero p {
          max-width: 620px;
          color: #64748b;
        }

        .ni-eyebrow {
          display: inline-flex;
          align-items: center;
          padding: 6px 11px;
          margin-bottom: 10px;
          border-radius: 999px;
          background: rgba(6,182,164,0.12);
          color: #0f766e;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .ni-breadcrumb,
        .ni-breadcrumb a,
        .ni-breadcrumb span {
          color: #64748b;
          font-size: 13px;
          text-decoration: none;
        }

        .ni-hero-panel {
          min-width: 220px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 18px;
          border-radius: 16px;
          background: rgba(255,255,255,0.8);
          border: 1px solid rgba(6,182,164,0.18);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.8);
        }

        .ni-status-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #10b981;
          box-shadow: 0 0 0 6px rgba(16,185,129,0.12);
        }

        .ni-card,
        .ni-order-ticket,
        .ni-table-card {
          border-radius: 18px !important;
          border: 1px solid #e2e8f0 !important;
          background: #ffffff;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.06);
        }

        .ni-filter-card {
          overflow: visible !important;
        }

        .ni-soft-btn {
          border: 1px solid #b3e8e2;
          color: #0f766e;
          background: #ecfdf5;
          font-weight: 700;
        }

        .ni-soft-btn:hover {
          background: #d9fbf3;
          color: #0f766e;
        }

        .ni-input-shell {
          min-height: 44px;
          border-color: #dbeafe !important;
          box-shadow: 0 1px 0 rgba(15,23,42,0.02);
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .ni-input-shell:focus-within {
          border-color: ${TEAL} !important;
          box-shadow: 0 0 0 4px rgba(6,182,164,0.12);
        }

        .ni-info-strip {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          padding: 12px 14px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px dashed #cbd5e1;
          color: #64748b;
          font-size: 13px;
        }

        .ni-info-strip span {
          padding-right: 10px;
          border-right: 1px solid #e2e8f0;
        }

        .ni-info-strip span:last-child {
          border-right: 0;
        }

        .ni-ticket-header {
          background:
            linear-gradient(135deg, #f8fafc 0%, #ecfdf5 100%) !important;
        }

        .ni-stepper {
          display: grid;
          grid-template-columns: repeat(4, minmax(140px, 1fr));
          gap: 10px;
        }

        .ni-step {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
        }

        .ni-step span {
          width: 24px;
          height: 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(6,182,164,0.12);
          color: #0f766e;
          font-size: 12px;
        }

        .ni-section {
          padding: 18px;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
        }

        .ni-section-title {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 16px;
          color: #1e293b;
          font-weight: 800;
        }

        .ni-section-title::before {
          content: "";
          width: 4px;
          height: 18px;
          border-radius: 999px;
          background: ${TEAL};
        }

        .ni-order-ticket label.form-label {
          color: #64748b !important;
          font-weight: 700;
          letter-spacing: 0.01em;
        }

        .ni-order-ticket .form-control,
        .ni-order-ticket .form-select,
        .ni-filter-card .form-control,
        .ni-filter-card .form-select {
          border-radius: 10px;
          min-height: 44px;
        }

        .ni-basic-chip {
          display: inline-flex;
          align-items: center;
          padding: 9px 12px;
          border-radius: 999px;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #475569;
          font-size: 12px;
          font-weight: 700;
        }

        .ni-sticky-actions {
          position: sticky;
          bottom: 0;
          margin: 0 -24px -24px;
          padding: 16px 24px !important;
          background: rgba(255,255,255,0.94);
          backdrop-filter: blur(10px);
          z-index: 2;
        }

        .ni-tab-btn {
          border: 0;
          border-radius: 999px;
          padding: 9px 16px;
          color: #fff;
          font-weight: 800;
          box-shadow: 0 8px 18px rgba(13,110,253,0.14);
        }

        .ni-tab-btn.active {
          box-shadow: 0 10px 24px rgba(6,182,164,0.22);
        }

        .ni-table-card {
          overflow: hidden;
        }

        .ni-table-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 20px;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
        }

        .ni-table-card table thead th {
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .ni-table-card table tbody tr {
          transition: background 0.2s ease, transform 0.2s ease;
        }

        .ni-table-card table tbody tr:hover {
          background: #f0fdfa !important;
        }

        .hover-bg-teal:hover { background: #ecfdf5 !important; color: #0d9488 !important; }

        @media (max-width: 992px) {
          .ni-hero {
            flex-direction: column;
          }

          .ni-stepper {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 576px) {
          .ni-hero {
            padding: 20px;
          }

          .ni-stepper {
            grid-template-columns: 1fr;
          }

          .ni-info-strip {
            display: block;
          }

          .ni-info-strip span {
            display: block;
            border-right: 0;
            padding: 3px 0;
          }
        }
      `}</style>
    </>
  );
}
